import { useState, useCallback, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import useApiFetch from '@/hooks/useApiFetch';
import permissionsService, {
  AccessLevel,
  PermissionMatrix,
  UserPermissionRow,
} from '@/services/permissions';
import { chakraExtendedTheme } from '@/styles/theme';
import PermissionsManagementComponent from './PermissionsManagement.component';

const PermissionsManagementContainer = function (): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'roles' | 'users'>('roles');

  const matrixResponse = useApiFetch<PermissionMatrix, void>(
    ['permissions-matrix'],
    async () => {
      const result = await permissionsService.getMatrix();
      return { data: result } as any;
    },
    { retry: false }
  );

  const matrix: PermissionMatrix = useMemo(() => {
    const raw = matrixResponse.data;
    const d = (raw as any)?.data ?? raw;
    return (d as PermissionMatrix) ?? { roles: [], modules: [], entries: [] };
  }, [matrixResponse.data]);

  const usersResponse = useApiFetch<UserPermissionRow[], void>(
    ['permissions-users', matrix.entries.length],
    async () => {
      const result = await permissionsService.getUserPermissions(matrix.entries);
      return { data: result } as any;
    },
    { retry: false, enabled: matrix.entries.length >= 0 }
  );

  const users: UserPermissionRow[] = useMemo(() => {
    const raw = usersResponse.data;
    const d = (raw as any)?.data ?? raw;
    return (d as UserPermissionRow[]) ?? [];
  }, [usersResponse.data]);

  const handleAccessChange = useCallback(
    async (roleId: string, module: string, access: AccessLevel) => {
      setSaving(true);
      try {
        await permissionsService.setAccess(roleId, module, access);
        matrixResponse.refetch();
      } finally {
        setSaving(false);
      }
    },
    [matrixResponse]
  );

  const handleSetOwner = useCallback(
    async (profileId: string) => {
      setSaving(true);
      try {
        await permissionsService.setOwner(profileId);
        usersResponse.refetch();
      } finally {
        setSaving(false);
      }
    },
    [usersResponse]
  );

  const handleRemoveOwner = useCallback(
    async (profileId: string) => {
      setSaving(true);
      try {
        await permissionsService.removeOwner(profileId);
        usersResponse.refetch();
      } finally {
        setSaving(false);
      }
    },
    [usersResponse]
  );

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <PermissionsManagementComponent
        matrix={matrix}
        users={users}
        isLoading={matrixResponse.isLoading}
        isUsersLoading={usersResponse.isLoading}
        isSaving={saving}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onAccessChange={handleAccessChange}
        onSetOwner={handleSetOwner}
        onRemoveOwner={handleRemoveOwner}
      />
    </ChakraProvider>
  );
};

export default PermissionsManagementContainer;
